import { createStoredSignal } from "@utils/createStoredSignal";
import {
	type Accessor,
	createContext,
	createMemo,
	type ParentComponent,
	useContext,
} from "solid-js";

export type AppLayoutKind =
	| "askgvt"
	| "studio"
	| "admin"
	| "labs"
	| "agency-hub"
	| "roster"
	| "account";

const LayoutAppContext = createContext<{
	appKind: Accessor<AppLayoutKind>;
	menuIsOpen: Accessor<boolean>;
	setMenuIsOpen: (isOpen: boolean) => void;
}>();

export const LayoutProvider: ParentComponent<{ app: AppLayoutKind }> = (
	props,
) => {
	const appKind = createMemo(() => props.app);
	const [menuIsOpen, setMenuIsOpen] = createStoredSignal("menuIsOpen", true);
	return (
		<LayoutAppContext.Provider value={{ appKind, menuIsOpen, setMenuIsOpen }}>
			{props.children}
		</LayoutAppContext.Provider>
	);
};

export function useLayoutAppKind(): AppLayoutKind {
	const ctx = useContext(LayoutAppContext);
	if (!ctx) {
		// Hack for Chat app video panel which is rendered inside the StudioLayout as a separate solidjs app.
		// Can be removed once the chat is converted to solidjs.
		if (document.querySelector("[data-app-kind]")) {
			return document
				.querySelector("[data-app-kind]")
				?.getAttribute("data-app-kind") as AppLayoutKind;
		}
		throw new Error("useLayoutAppKind must be used within LayoutProvider");
	}

	return ctx.appKind();
}

/** Like {@link useLayoutAppKind} but returns undefined outside a layout tree. */
export function useLayoutAppKindOptional(): Accessor<AppLayoutKind | undefined> {
	const ctx = useContext(LayoutAppContext);
	return createMemo(() => {
		if (ctx) {
			return ctx.appKind();
		}
		const attr = document
			.querySelector("[data-app-kind]")
			?.getAttribute("data-app-kind");
		return attr ? (attr as AppLayoutKind) : undefined;
	});
}

export const useAppContext = () => {
	const ctx = useContext(LayoutAppContext);
	if (!ctx) {
		throw new Error("useMainMenu must be used within LayoutProvider");
	}
	return ctx;
};

/** Layout menu state when inside a {@link LayoutProvider}, otherwise `undefined`. */
export const useAppContextOptional = () => useContext(LayoutAppContext);
